import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import Icon from "../../components/AppIcon";
import { apiClient } from "../../services/api/client";
import { useTenantContext } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import InviteUserModal from "./components/InviteUserModal";
import EditUserModal from "./components/EditUserModal";

const ROLE_OPTIONS = [
  { value: "User", label: "User", description: "Basic access to view data" },
  {
    value: "Manager",
    label: "Manager",
    description: "Can manage employees and distributions",
  },
  {
    value: "TenantAdmin",
    label: "Administrator",
    description: "Full tenant administration access",
  },
];

// Helper to get display-friendly role names
const getRoleDisplayName = (role) => {
  const map = {
    User: "User",
    Manager: "Manager",
    TroncMaster: "TRONC Master",
    TenantAdmin: "Administrator",
    GlobalAdmin: "Global Admin",
  };
  return map[role] || role;
};

const getRoleBadgeClass = (role) => {
  const map = {
    GlobalAdmin: "badge-error",
    TenantAdmin: "badge-primary",
    TroncMaster: "badge-warning",
    Manager: "badge-secondary",
    User: "badge-neutral",
  };
  return map[role] || "badge-neutral";
};

const UserManagement = () => {
  const { selectedTenant, isGlobalAdmin, needsTenantSelection } =
    useTenantContext();
  const { isGlobalAdmin: currentUserIsGlobalAdmin, isTenantAdmin: currentUserIsTenantAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const shouldFetchData = true;

  useEffect(() => {
    fetchUsers();
    fetchInvitations();
  }, [selectedTenant?.id]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get("/users");
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/users/invitations");
      if (response.success) {
        setInvitations(response.data || []);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch invitations");
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSent = () => {
    setShowInviteModal(false);
    fetchInvitations();
    showNotification("Invitation sent successfully!");
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
  };

  const handleEditSuccess = () => {
    setEditingUser(null);
    fetchUsers();
    showNotification("User updated successfully!");
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const response = await apiClient.post(
        `/users/invitations/${invitationId}/resend`,
      );
      if (response.success) {
        showNotification("Invitation resent successfully!");
        fetchInvitations();
      } else {
        showNotification(
          response.message || "Failed to resend invitation",
          "error",
        );
      }
    } catch (err) {
      showNotification(err.message || "Failed to resend invitation", "error");
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!confirm("Are you sure you want to revoke this invitation?")) return;

    try {
      const response = await apiClient.post(
        `/users/invitations/${invitationId}/revoke`,
      );
      if (response.success) {
        showNotification("Invitation revoked successfully!");
        fetchInvitations();
      } else {
        showNotification(
          response.message || "Failed to revoke invitation",
          "error",
        );
      }
    } catch (err) {
      showNotification(err.message || "Failed to revoke invitation", "error");
    }
  };

  return (
    <PageLayout>
      <Helmet>
        <title>User Management</title>
      </Helmet>
      <PageSection>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold">User Management</h1>
          {(currentUserIsGlobalAdmin || currentUserIsTenantAdmin) && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn btn-primary"
            >
              Invite User
            </button>
          )}
        </div>

        <div className="tabs mb-6 md:mb-10">
          <button
            className={`tab ${activeTab === "users" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            Users
          </button>
          <button
            className={`tab ${activeTab === "invitations" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("invitations")}
          >
            Invitations
          </button>
        </div>

        {notification && (
          <div
            className={`alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
            {notification.message}
          </div>
        )}

        {activeTab === "users" && (
          <div className="w-full overflow-x-auto rounded-lg border">
            <table className="table w-full min-w-[1100px]">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Role</th>
                  {currentUserIsGlobalAdmin && <th className="px-4 py-2">Tenant</th>}
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan={currentUserIsGlobalAdmin ? 6 : 5}
                      className="px-4 py-2 text-center text-muted"
                    >
                      No users found.
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-muted">
                    <td className="px-4 py-2 font-medium">
                      {user.fullName || `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "—"}
                    </td>
                    <td className="px-4 py-2">{user.email}</td>
                    <td className="px-4 py-2">
                      <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    {currentUserIsGlobalAdmin && (
                      <td className="px-4 py-2">
                        {user.tenantName ? (
                          <span className="text-sm">{user.tenantName}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">System</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2">
                      {user.isActive ? (
                        <span className="text-green-500">Active</span>
                      ) : (
                        <span className="text-red-500">Inactive</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {user.isActive && (
                        <div className="flex items-center justify-start gap-1">
                          <button
                            className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
                            onClick={() => handleEditUser(user)}
                            title="Edit user"
                          >
                            <Icon name="Pencil" size={16} />
                          </button>
                          <button
                            className="p-2 hover:bg-error/10 rounded-md text-error"
                            onClick={() => handleDeactivateUser(user.id)}
                            title="Deactivate user"
                          >
                            <Icon name="UserMinus" size={16} />
                          </button>
                        </div>
                      )}
                      {!user.isActive && (
                        <button
                          className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
                          onClick={() => handleEditUser(user)}
                          title="Edit user"
                        >
                          <Icon name="Pencil" size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "invitations" && (
          <div className="w-full overflow-x-auto rounded-lg border">
            <table className="table w-full min-w-[1100px]">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Role</th>
                  {currentUserIsGlobalAdmin && <th className="px-4 py-2">Tenant</th>}
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={currentUserIsGlobalAdmin ? 6 : 5} className="px-4 py-2 text-center">
                      <div className="loader"></div>
                    </td>
                  </tr>
                )}
                {!loading && invitations.length === 0 && (
                  <tr>
                    <td
                      colSpan={currentUserIsGlobalAdmin ? 6 : 5}
                      className="px-4 py-2 text-center text-muted"
                    >
                      No invitations found.
                    </td>
                  </tr>
                )}
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-muted">
                    <td className="px-4 py-2 font-medium">
                      {invitation.fullName || `${invitation.firstName ?? ""} ${invitation.lastName ?? ""}`.trim() || "—"}
                    </td>
                    <td className="px-4 py-2">{invitation.email}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`badge ${getRoleBadgeClass(invitation.role)}`}
                      >
                        {getRoleDisplayName(invitation.role)}
                      </span>
                    </td>
                    {currentUserIsGlobalAdmin && (
                      <td className="px-4 py-2">
                        {invitation.tenantName ? (
                          <span className="text-sm">{invitation.tenantName}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">System</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2">
                      {invitation.status === "Pending" && !invitation.isExpired && (
                        <span className="badge badge-warning">Pending</span>
                      )}
                      {invitation.isExpired && (
                        <span className="badge badge-neutral">Expired</span>
                      )}
                      {invitation.status === "Accepted" && (
                        <span className="badge badge-success">Accepted</span>
                      )}
                      {invitation.status === "Revoked" && (
                        <span className="badge badge-error">Revoked</span>
                      )}
                      {!invitation.status && (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-start gap-2">
                        {invitation.status === "Pending" && !invitation.isExpired && (
                          <button
                            className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
                            onClick={() => handleResendInvitation(invitation.id)}
                            title="Resend invitation"
                          >
                            <Icon name="Send" size={16} />
                          </button>
                        )}
                        {invitation.status !== "Accepted" && (
                          <button
                            className="p-2 hover:bg-error/10 rounded-md text-error"
                            onClick={() => handleRevokeInvitation(invitation.id)}
                            title="Revoke invitation"
                          >
                            <Icon name="XCircle" size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSent}
          roleOptions={ROLE_OPTIONS}
          isGlobalAdmin={currentUserIsGlobalAdmin}
          tenantId={selectedTenant?.id ?? null}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={handleEditSuccess}
          isGlobalAdmin={currentUserIsGlobalAdmin}
          currentUserRole={
            currentUserIsGlobalAdmin ? "GlobalAdmin" : "TenantAdmin"
          }
        />
      )}
    </PageLayout>
  );
};

export default UserManagement;
